import * as Sentry from "@sentry/react";
import { wasPreloadErrorHandled } from "./app-version-error-deduplication";
import {
  getAppErrorData,
  isExpectedAppError,
  isExpectedUnauthenticatedError,
} from "./convex-errors";

/**
 * The prefix the Convex client puts on every failed call's message:
 * `[CONVEX Q(module:fn)] [Request ID: 3de4204bf89b44f5] Server Error`. Production redacts the
 * rest, platform faults included, so the function and request ID are all the browser learns.
 */
const CONVEX_CALL_PREFIX = /\[CONVEX [A-Z]+\(([\w./-]+:[\w./-]+)\)\](?: \[Request ID: (\w+)\])?/;

type ConvexCall = { functionPath: string; requestId?: string };

export function shouldDropSentryEvent(error: unknown): boolean {
  return isExpectedUnauthenticatedError(error) || isExpectedAppError(error);
}

function errorText(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message;
  return undefined;
}

/**
 * Reads the failed Convex call from the event's own error text. Breadcrumbs are not read: they
 * name earlier calls, which would pin this failure on the wrong function.
 */
function findConvexCall(event: Sentry.Event, hint?: Sentry.EventHint): ConvexCall | undefined {
  const candidates = [
    errorText(hint?.originalException),
    ...(event.exception?.values ?? []).map((exception) => exception.value),
    event.message,
  ];
  for (const text of candidates) {
    const match = text ? CONVEX_CALL_PREFIX.exec(text) : null;
    if (match?.[1]) return { functionPath: match[1], requestId: match[2] };
  }
  return undefined;
}

/**
 * Tags a failed Convex call with `func` and `request_id`, the tag names Convex's log-stream
 * reporter puts on the server side of the same call, so `request_id:<id>` finds both events and
 * the ID is the `df-convex` key for the unredacted error. Coded app errors are also fingerprinted
 * by code + function, so distinct server failures of one operation stop collapsing into a single
 * issue behind the redacted "Server Error" message. Request IDs never go into a fingerprint.
 */
export function applyConvexSentryContext<TEvent extends Sentry.Event>(
  event: TEvent,
  hint?: Sentry.EventHint
): TEvent {
  const call = findConvexCall(event, hint);
  if (call) {
    event.tags = {
      ...event.tags,
      func: call.functionPath,
      ...(call.requestId && { request_id: call.requestId }),
    };
  }

  const appError = getAppErrorData(hint?.originalException);
  if (appError) {
    event.tags = { ...event.tags, app_error_code: appError.code };
    event.fingerprint = [
      "convex-app-error",
      appError.code,
      call?.functionPath ?? "unknown_function",
    ];
  }
  return event;
}

type SentryEnvironment = "production" | "preview" | "uat" | "demo" | "development";

function isSentryEnvironment(value: unknown): value is SentryEnvironment {
  return (
    value === "production" ||
    value === "preview" ||
    value === "uat" ||
    value === "demo" ||
    value === "development"
  );
}

function resolveEnvironment(): SentryEnvironment {
  const dfEnvironment = import.meta.env.VITE_DF_ENVIRONMENT;
  if (isSentryEnvironment(dfEnvironment)) return dfEnvironment;

  const vercelEnv = import.meta.env.VITE_VERCEL_ENV;
  if (vercelEnv === "production" || vercelEnv === "preview") return vercelEnv;
  if (isSentryEnvironment(import.meta.env.MODE)) {
    return import.meta.env.MODE;
  }
  return "development";
}

export function initSentry() {
  if (import.meta.env.DEV) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  const environment = resolveEnvironment();
  // Application-owned tags shared with the Convex backend, whose Sentry events come from
  // Convex's managed log stream (no SDK of ours): `df_environment` and `df_release` (the full
  // commit SHA — `RELEASE` on Convex, `VITE_BUILD_ID` here; vite.config.ts defines it as
  // `local` for builds without a SHA). Sentry's native `release` stays
  // `web-app@<sha7>` for release health; `df_release:<sha>` is the one search that matches
  // browser and backend events of the same deploy.
  const buildRelease = import.meta.env.VITE_BUILD_ID || "unknown";

  Sentry.init({
    dsn,
    environment,
    release: import.meta.env.VITE_RELEASE,

    beforeSend(event, hint) {
      const error = hint.originalException;

      // Missing identity is expected auth state, and coded app errors the UI
      // handles as normal outcomes (invalid input, conflicts, stale versions)
      // are expected failures. Other auth errors, such as INVALID_AUTH, can
      // indicate JWT configuration problems and stay visible, as do app-error
      // codes that signal bugs or infrastructure trouble.
      if (shouldDropSentryEvent(error)) {
        return null;
      }

      if (wasPreloadErrorHandled(error) && event.tags?.feature !== "app-version-recovery") {
        return null;
      }

      return applyConvexSentryContext(event, hint);
    },
  });

  Sentry.setTag("df_environment", environment);
  Sentry.setTag("df_release", buildRelease);
}

export function setSentryIdentity(input: { userId?: string; orgId?: string }) {
  Sentry.setUser(input.userId ? { id: input.userId } : null);
  Sentry.setTag("orgId", input.orgId ?? "unknown");
}

export function setSentryAppContext(input: { routeFamily?: string; workspaceKind?: string }) {
  Sentry.setTag("routeFamily", input.routeFamily ?? "unknown");
  Sentry.setTag("workspaceKind", input.workspaceKind ?? "none");
  Sentry.setContext("app", {
    routeFamily: input.routeFamily ?? "unknown",
    workspaceKind: input.workspaceKind ?? "none",
  });
}
